import { useId, useMemo, useState } from 'react';
import { matchesDirectoryQuery } from '../lib/search';

export interface SearchComboboxOption {
  id: string;
  label: string;
  aliases: string[];
  context?: string;
}

interface SearchComboboxProps {
  label: string;
  placeholder: string;
  options: SearchComboboxOption[];
  value: string | null;
  disabled?: boolean;
  emptyMessage: string;
  onChange: (id: string | null) => void;
}

export function SearchCombobox({
  label,
  placeholder,
  options,
  value,
  disabled = false,
  emptyMessage,
  onChange,
}: SearchComboboxProps) {
  const inputId = useId();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const selected = options.find((option) => option.id === value) ?? null;
  const visibleOptions = useMemo(
    () =>
      options.filter((option) =>
        matchesDirectoryQuery(
          {
            label: option.label,
            aliases: option.aliases,
            context: option.context,
          },
          query,
        ),
      ),
    [options, query],
  );

  const displayValue = open ? query : selected?.label ?? '';

  return (
    <div className="combobox-field">
      <label htmlFor={inputId}>{label}</label>
      <div className="combobox-control">
        <input
          id={inputId}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={displayValue}
          onFocus={() => {
            setQuery('');
            setOpen(true);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            if (value) {
              onChange(null);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
        />
        {value ? (
          <button
            className="combobox-clear"
            type="button"
            aria-label={`Clear ${label}`}
            onClick={() => {
              setQuery('');
              setOpen(false);
              onChange(null);
            }}
          >
            ×
          </button>
        ) : null}
      </div>

      {open && !disabled ? (
        <div className="combobox-popover" id={listId} role="listbox">
          {visibleOptions.length ? (
            visibleOptions.map((option) => (
              <button
                key={option.id}
                className="combobox-option"
                type="button"
                role="option"
                aria-selected={option.id === value}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(option.id);
                  setQuery('');
                  setOpen(false);
                }}
              >
                <strong>{option.label}</strong>
                {option.context ? <small>{option.context}</small> : null}
              </button>
            ))
          ) : (
            <p className="combobox-empty">{emptyMessage}</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
